import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as iService } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { notifyUserError, setDefaultCustomField } from 'beebee-base/lib/utility';
import moment from 'moment-timezone';

export default class FormStepProjectComponent extends Component {
  @iService session;
  @iService store;
  @iService notify;
  @iService intl;
  @iService log;

  @tracked newProject = null;
  @tracked loadError = false;

  constructor(owner, args) {
    super(owner, args);
    this.initProjectTask.perform();
  }

  @(task(function* () {
    yield timeout(50);
    this.loadError = false;
    try {
      const projectStateId = this.args.step?.id;
      const me = this.session.me;

      let projectState = this.store.peekRecord('project_state', projectStateId);
      if (!projectState) {
        projectState = yield this.store.findRecord('project_state', projectStateId);
      }

      const org = me.organization;
      if (!org?.organization_custom_fields?.length) {
        yield this.session.reloadMeTask.perform();
      }

      const newModel = this.store.createRecord('project', {
        start_date: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
        project_state: projectState,
        name: moment().format('DD/MM/YYYY'),
        color: projectState.get('status_color') || null,
      });

      yield setDefaultCustomField(me, newModel, this.store);

      const contacts = yield newModel.contacts;
      contacts.push(me);

      let casoClinicoState = this.store.peekRecord('project_state', '100');
      if (!casoClinicoState) {
        try {
          casoClinicoState = yield this.store.findRecord('project_state', '100');
        } catch {
          casoClinicoState = null;
        }
      }

      if (casoClinicoState) {
        const casi = yield this.store.query('project', {
          status: casoClinicoState.id,
          contact_id: me.id,
        });

        const casiArray = casi ? casi.slice() : [];
        let casoClinico = casiArray.length > 0 ? casiArray[casiArray.length - 1] : null;

        if (!casoClinico) {
          const cc = this.store.createRecord('project', {
            start_date: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
            project_state: casoClinicoState,
            name: `Caso Clinico ${moment().format('DD/MM/YYYY')}`,
          });
          const ccContacts = yield cc.contacts;
          ccContacts.push(me);
          const savedItem = yield this.args.applicationController.saveRecordRelated(cc, false);
          // saveRecordRelated resolves `false` on failure — it never rejects, so
          // a settled promise is not a saved record. Falling through here read
          // `false[0]` (undefined on a boxed primitive, no throw), left `ccId`
          // undefined and linked the new project to the unsaved `cc`, which has
          // no id: the step then advanced and saveTask's saveAll wrote the
          // booking against a parent project that does not exist.
          if (savedItem === false) {
            this.loadError = true;
            return;
          }
          const ccId = savedItem[0]?.data?.id;
          if (ccId) {
            casoClinico = this.store.peekRecord('project', String(ccId));
          }
          if (!casoClinico) {
            casoClinico = cc;
          }
        }

        const relatedProjects = yield newModel.related_projects;
        relatedProjects.push(casoClinico);
      }

      this.newProject = newModel;
    } catch (e) {
      this.log.error('[form-step-project] initProjectTask error:', e);
      this.loadError = true;
    }
  }).restartable())
  initProjectTask;

  @(task(function* () {
    const project = this.newProject;
    if (!project) return;
    try {
      yield project.saveAll(project);
      this.args.onNext();
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
    }
  }).drop())
  saveTask;

  @action
  next() {
    this.saveTask.perform();
  }
}
